/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import React, { FunctionComponent, useEffect, useState } from 'react';

import { Diagnostics } from '@tivocorp/tivo-webapp-loggers/lib/Diagnostics';
import { EpisodeInfo } from 'client-code/episode-guide';
import { UpcomingOffer, UpcomingOfferV2 } from 'client-code/upcoming-offers';
import { VodItem } from 'client-code/vod-browse';

import { DeviceModel } from '../../core/device';
import { Log } from '../../core/logging';
import {
    CursorManager,
    KeyManager,
    ScreenControllerTypes,
    ScreenTaskControllerTypes,
} from '../../core/navigation';
import { ScreenController } from '../../core/navigation/ScreenController';
import { SharedPrefConstants } from '../../core/storage';
import { RootState, useTivoDispatch, useTivoSelector } from '../../core/tivo/tivo-redux-wrapper';
import { ListView } from '../../core/usecases';
import { clearActiveInteractiveComponent } from '../../store/interactivelayer/interactiveLayer.reducers';
import { TListViewScreen } from '../../store/listscreenview';
import { setListEntryActions } from '../../store/listscreenview/listviewscreen.actions';
import { listScreenSetCaretInvisible } from '../../store/listscreenview/listviewscreen.reducers';
import { showWhisper } from '../../store/whisper/whisper.reducer';
import { ClockTypes } from '../Clock';
import { AntiburnOsd, ScreenHeader } from '../common';
import { useScreenController } from '../hooks';
import { Whisper } from '../Whisper';
import { WhisperTypes } from '../Whisper/WhisperTypes';
import { InteractiveLayer } from './InteractiveLayer';
import OnDemandList from './OnDemandList/OnDemandList';
import UpcomingList from './UpcomingList/UpcomingList';
import VodListView from './VodListView/VodListView';

import './ListViewScreen.scss';

const ListViewScreen: FunctionComponent = (): JSX.Element => {
    const TAG = 'ListViewScreen';

    const MAX_ELEMENTS_IN_ONE_PAGE_FOR_ON_DEMAND = 19;

    const dispatch = useTivoDispatch();
    const [hasError, setHasError] = useState<boolean>(false);

    const [errorMessage, setErrorMessage] = useState<string>();

    const [isLoading, setIsLoading] = useState<boolean>(true);

    const ListViewScreenIns = useScreenController<TListViewScreen>({
        identifier: TAG,
        properties: {
            screenController: ScreenControllerTypes.LIST_VIEW_SCREEN,
            taskController: ScreenTaskControllerTypes.DEFAULT_LIST_SCREEN_TASK,
        },
    });
    const { isActive: isAntiBurnOsdActive } = useTivoSelector(
        (state: RootState) => state.antiburnOsd,
    );
    const { id, title, listType } = ListViewScreenIns?.screen?.getProperties() || {};
    const { position, limit, page, ghostMode } = useTivoSelector(
        (state: RootState) => state.listViewScreen,
    );
    const [items, setItems] = useState<
        VodItem[] | UpcomingOffer[] | UpcomingOfferV2[] | EpisodeInfo[] | []
    >();
    const offerIdRef = React.useRef<string>('');
    const list = new ListView();

    const handleListViewInit = async () => {
        try {
            if (!id) return;
            if (listType === 'upcoming') {
                const { upcomingOffers } = await list.getUpcomingData(id || '');
                const newItems = upcomingOffers || [];
                setItems(newItems);
            } else if (
                listType === 'myRentals' ||
                listType === 'myadultRentals' ||
                listType === 'textCurrentRentals'
            ) {
                const { vodItemList } = await list.getMyRentalsVodBrowseData(id || '');
                const newItems = vodItemList?.vodItemList || [];
                setItems(newItems);
                offerIdRef.current =
                    (newItems && (newItems[position]?.vodItemKernel as any))?.offerId || '';
            } else {
                Log.w(TAG, `Unsupported listType: ${listType}`);
            }
        } catch (error: any) {
            setHasError(true);
            const message = error.message.replace('SCREEN_TYPE', title || '');
            setErrorMessage(message);
            dispatch(showWhisper({ message, iconType: WhisperTypes.INFO }));
            setItems([]);
        }
    };

    const onCursorStateChangeCallback = (state: boolean) => {
        if (state === false) {
            dispatch(clearActiveInteractiveComponent());
            dispatch(listScreenSetCaretInvisible());
        }
    };

    useEffect(() => {
        dispatch(setListEntryActions(0));
        KeyManager.getInstance().stopKeyIgnoring();
        const refCursormanager = CursorManager.subscribe(onCursorStateChangeCallback);
        handleListViewInit();
        Diagnostics.info(TAG, `${title} mounted.`);
        return () => {
            CursorManager.unsubscribe(refCursormanager);
            setItems([]);
            setHasError(false);
            Diagnostics.info(TAG, `${title} unmounted.`);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const fetchListData = async () => {
            try {
                if (!id) return;
                if (listType === 'upcoming') {
                    const { upcomingOffers } = await list.getUpcomingData(
                        id || '',
                        page * limit,
                        (page + 1) * limit,
                    );

                    const newItems = upcomingOffers || [];
                    setItems(newItems);
                    offerIdRef.current = newItems && (newItems[position]?.offerId || '');
                } else if (
                    listType === 'myRentals' ||
                    listType === 'myadultRentals' ||
                    listType === 'textCurrentRentals'
                ) {
                    const { vodItemList } = await list.getMyRentalsVodBrowseData(
                        id || '',
                        page * limit,
                        (page + 1) * limit,
                    );
                    const newItems = vodItemList?.vodItemList || [];
                    setItems(newItems);
                    offerIdRef.current =
                        (newItems && (newItems[position]?.vodItemKernel as any))?.offerId || '';
                } else if (listType === 'onDemand') {
                    const currScreen = ScreenController.getInstance().getScreen();
                    const currScreenProperties = currScreen.getProperties() as any;
                    const seasonData = await list.getSeasonData(
                        id,
                        currScreenProperties.partnerId,
                        currScreenProperties.collapsedVodItemId,
                        page * MAX_ELEMENTS_IN_ONE_PAGE_FOR_ON_DEMAND,
                        (page + 1) * MAX_ELEMENTS_IN_ONE_PAGE_FOR_ON_DEMAND,
                    );
                    setItems(seasonData);
                    setIsLoading(false);
                } else {
                    Log.w(TAG, `Unsupported listType: ${listType}`);
                }
            } catch (error: any) {
                setItems([]);
                const message = error.message.replace('SCREEN_TYPE', title || '');
                setHasError(true);
                dispatch(showWhisper({ message, iconType: WhisperTypes.INFO }));
                setErrorMessage(message);
            }
        };
        setItems([]);

        fetchListData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, id]);

    useEffect(() => {
        if (!items || (items && items?.length === 0)) return;
        const currentItem = items[position];

        if (currentItem && 'vodItemKernel' in currentItem && currentItem.vodItemKernel) {
            offerIdRef.current = (currentItem.vodItemKernel as any).offerId || '';
        } else if (currentItem && 'offerId' in currentItem) {
            offerIdRef.current = (currentItem as any).offerId || '';
        } else {
            offerIdRef.current = '';
        }
        const listScreenSharedPreferenceEditor = DeviceModel.getSharedPreferences(
            SharedPrefConstants.LIST_SCREEN_PREF_KEY,
        ).edit();
        const selectedItem = { ...(items && items[position]), type: listType };
        listScreenSharedPreferenceEditor.putJson(
            SharedPrefConstants.LIST_VIEW_SCREEN_SELECTED_OPTION,
            selectedItem as unknown as JSON,
        );
        listScreenSharedPreferenceEditor.commit();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items, position]);

    return (
        <div className="list-wrapper">
            <ScreenHeader title={title || ''} clockType={ClockTypes.DEFAULT_CLOCK} />
            <div className="list">
                {hasError ? (
                    <span className="error-message">{errorMessage}</span>
                ) : (
                    <div key={`${position}_list-view`}>
                        {listType === 'myRentals' ||
                            listType === 'myadultRentals' ||
                            (listType === 'textCurrentRentals' && (
                                <VodListView
                                    items={items}
                                    position={position}
                                    ghostMode={ghostMode}
                                />
                            ))}
                        {listType === 'upcoming' && (
                            <UpcomingList
                                items={items}
                                selectedIndex={position}
                                ghostMode={ghostMode}
                            />
                        )}
                        {listType === 'onDemand' && (
                            <OnDemandList
                                episodes={items as EpisodeInfo[]}
                                title={title || ''}
                                isLoading={isLoading}
                                ghostMode={ghostMode}
                            />
                        )}
                    </div>
                )}
            </div>
            {DeviceModel.isWebOsPlatform() && CursorManager.isCursorAppeared() ? (
                <InteractiveLayer />
            ) : null}
            {isAntiBurnOsdActive && <AntiburnOsd />}
            <Whisper message="" />
        </div>
    );
};

export default ListViewScreen;
